/**
 * Auth-Hydration Gate — single point of synchronisation between the boot-time
 * whoami round-trip (AuthHydrationService) and any code path that depends on
 * a hydrated `appState.user` (router enter-guards, ha-app connectedCallback).
 *
 * Why this exists:
 *
 * The original boot sequence used a top-level `await` in `main.ts` to block
 * component registration until hydration finished. That works for the import
 * graph of `main.ts` itself but does NOT reliably gate the upgrade of the
 * `<ha-app>` element already in the DOM, nor side effects of imported
 * packages (e.g. lottie-player auto-registering custom elements). The result
 * was a race where the router ran `checkForAuthentication()` against a stale
 * (null) `appState.user` and redirected to `/auth/login` even though a valid
 * auto-login cookie was about to populate the state.
 *
 * Contract:
 *
 *  - `setAuthHydrationPromise(promise)` is called exactly once during boot
 *    (in `main.ts`) with the in-flight hydration promise.
 *  - `awaitAuthHydration()` resolves once hydration completed (success or
 *    failure — the gate never rejects, so callers stay simple).
 *  - Calling `awaitAuthHydration()` before `setAuthHydrationPromise()` is
 *    safe and resolves once the setter is invoked.
 */

let resolveGate: (() => void) | null = null;
const gatePromise: Promise<void> = new Promise<void>((resolve) => {
    resolveGate = resolve;
});

let hydrationStarted = false;

/**
 * Registers the in-flight hydration promise. Subsequent waiters released
 * once it settles. Calling more than once is a no-op (defensive — prevents
 * re-entry corrupting the gate state during HMR or test re-imports).
 */
export const setAuthHydrationPromise = (promise: Promise<unknown>): void => {
    if (hydrationStarted) {
        return;
    }
    hydrationStarted = true;

    // Always resolve — never reject. Hydration failure is a normal outcome
    // (no cookie / 401 / network down) and must not poison every consumer.
    // Using `then(onFulfilled, onRejected)` rather than `.finally()` so the
    // rejection is consumed here and does not propagate as an unhandled
    // rejection in environments that monitor those (Vitest, browsers).
    const release = (): void => {
        if (resolveGate) {
            resolveGate();
            resolveGate = null;
        }
    };
    promise.then(release, release);
};

/**
 * Awaits the hydration gate. Returns immediately if already resolved.
 * Never rejects. Callers must not assume `appState.user` is populated —
 * they should check `appState.isUserLoggedIn` after awaiting.
 */
export const awaitAuthHydration = (): Promise<void> => gatePromise;
